/**
 * /terms — public terms of service.
 *
 * Required by Apple + Google for store submission. Linked from the
 * deletion landing page, privacy page, signup screens, and store
 * listings.
 *
 * Standard SaaS terms with WYDM-specific clauses: 18+ gate, anonymous
 * ratings carve-out, AI review disclaimer, premium subscription
 * cancellation policy mirroring Stripe + Apple + Google rules.
 *
 * NOT legal advice. Get a solicitor to review before public launch
 * in any regulated market.
 */
import { Link } from 'react-router-dom';

const LAST_UPDATED = '22 May 2026';

export function TermsPage() {
  return (
    <main style={page}>
      <div style={banner}>
        <strong>Pre-launch draft.</strong> These terms have not been reviewed by
        legal counsel. Review with a solicitor before public launch.
      </div>

      <h1 className="serif" style={h1}>Terms of Service</h1>
      <p style={meta}>Last updated: {LAST_UPDATED}</p>

      <p style={para}>
        Welcome to WYDM. These terms ("<strong>Terms</strong>") govern your use
        of the WYDM service — the iOS app, Android app, website at wydm.app, and
        related features ("<strong>Service</strong>"). The Service is operated by
        Callo Apps Ltd, a company registered in the United Kingdom
        ("<strong>we</strong>", "<strong>us</strong>"). By creating an account or
        using the Service you agree to these Terms.
      </p>

      <h2 className="serif" style={h2}>1. Eligibility</h2>
      <p style={para}>
        You must be at least <strong>18 years old</strong> to use WYDM. By creating
        an account you confirm you meet this requirement. We collect a date of
        birth at signup to enforce the 18+ gate; submitting a false date of
        birth is a breach of these Terms and will result in account termination.
      </p>

      <h2 className="serif" style={h2}>2. Your account</h2>
      <ul style={ul}>
        <li>You're responsible for activity under your account. Keep your password secret. If you suspect compromise, change it immediately and email <a href="mailto:support@wydm.app" style={inlineLink}>support@wydm.app</a>.</li>
        <li>One account per person. Creating duplicate accounts to evade bans, ratings, or rate-limits is prohibited.</li>
        <li>Account credentials are not transferable. You may not sell, rent, or share your account.</li>
      </ul>

      <h2 className="serif" style={h2}>3. Your content</h2>
      <p style={para}>
        "<strong>Your Content</strong>" means anything you upload to WYDM —
        photos, bio text, messages, ratings notes, anything you type or attach.
      </p>
      <ul style={ul}>
        <li><strong>You own Your Content.</strong> You retain copyright and any other rights.</li>
        <li>You grant us a worldwide, non-exclusive, royalty-free licence to host, store, copy, display, distribute, and process Your Content solely to operate and improve the Service. This licence ends when you delete the content or close your account (subject to backup-purge timelines documented in the <Link to="/privacy" style={inlineLink}>Privacy Policy</Link>).</li>
        <li>You're responsible for Your Content — that you own it or have permission to upload it, and that it complies with these Terms.</li>
        <li>You may not upload content that infringes someone else's rights, is illegal, sexually explicit, hateful, harassing, threatening, deceptive, or impersonates another person.</li>
        <li>All uploaded photos are scanned for child sexual abuse material (CSAM) via Cloudflare's free scanner. Confirmed matches are reported to the National Center for Missing & Exploited Children (NCMEC) and result in immediate, permanent account termination.</li>
        <li>All uploaded photos pass through NSFW detection before becoming publicly visible. Sexually explicit images are rejected.</li>
      </ul>

      <h2 className="serif" style={h2}>4. Anonymous ratings</h2>
      <p style={para}>
        The core WYDM mechanic is anonymous feedback on dating profiles. Some
        specific rules:
      </p>
      <ul style={ul}>
        <li><strong>Ratings of you are anonymous to you.</strong> We will not disclose who rated your profile, with one exception: if a Premium user starts a DM conversation with you, their identity is revealed to you at the moment of the first message.</li>
        <li><strong>Notes attached to ratings are public to the profile owner.</strong> Don't include anything you wouldn't want the recipient to read.</li>
        <li>Notes are screened by an AI moderator for hate speech, threats, and harassment. Notes flagged as problematic are held pending human review and never delivered if confirmed.</li>
        <li>Brigading (organising groups to rate someone negatively) and grading-based bullying are prohibited and grounds for account termination.</li>
      </ul>

      <h2 className="serif" style={h2}>5. Premium subscription</h2>
      <ul style={ul}>
        <li>WYDM Premium unlocks AI reviews and direct messages. Subscriptions are sold exclusively through the iOS and Android apps via the App Store and Google Play. Pricing is visible on the in-app paywall (and on the store listing) before purchase.</li>
        <li>Premium auto-renews until cancelled. Cancellation:
          <ul style={ulNested}>
            <li>iOS (App Store): cancel via Settings → [your Apple ID] → Subscriptions → WYDM Premium → Cancel. Apple's terms govern refunds.</li>
            <li>Android (Google Play): cancel via Play Store → Profile → Payments &amp; subscriptions → Subscriptions. Google's terms govern refunds.</li>
          </ul>
        </li>
        <li>If you delete your account during an active Premium period, your App Store / Google Play subscription continues until you cancel it in OS settings — we cannot cancel store subscriptions on your behalf.</li>
        <li>All refunds are handled by Apple and Google through their respective storefronts.</li>
        <li>We may change pricing with at least 30 days' notice via email or in-app notification. Continued use after the change date constitutes acceptance.</li>
      </ul>

      <h2 className="serif" style={h2}>6. AI reviews</h2>
      <p style={para}>
        WYDM Premium includes AI-generated reviews of your own profile, written
        by language models in three distinct voices (Honest Friend, Brutal
        Critic, Hype Man). Some things to know:
      </p>
      <ul style={ul}>
        <li>AI reviews are <strong>generated by third-party language models</strong> (currently Anthropic Claude). The model receives a snapshot of your profile (name, age, gender, looking-for, bio, photos, music) and produces text in response.</li>
        <li>The output may contain factual errors, awkward phrasing, or opinions you disagree with. It's a tool to help you think about your profile, not authoritative advice.</li>
        <li>The Brutal Critic personality is intentionally direct. If you don't want sharp feedback, don't generate that personality.</li>
        <li>Reviews are private to you (the profile owner). We don't share them with raters or anyone else.</li>
      </ul>

      <h2 className="serif" style={h2}>7. Direct messages</h2>
      <ul style={ul}>
        <li>DMs require Premium to initiate. You may only DM a user whose profile you've rated.</li>
        <li>Sending the first DM reveals your identity (display name, profile) to the recipient.</li>
        <li>Either party may block the conversation, ending it for both sides.</li>
        <li>Either party may report a message that violates these Terms. We may review and act on reports up to and including account termination.</li>
        <li>We don't pre-screen DMs for content. By participating you accept that the other party may say things you don't want to hear; block and report tools are at your disposal.</li>
      </ul>

      <h2 className="serif" style={h2}>8. Prohibited conduct</h2>
      <p style={para}>You may not:</p>
      <ul style={ul}>
        <li>Use the Service to harass, threaten, defame, or stalk any person.</li>
        <li>Solicit sexual services, advertise unrelated products, or send unsolicited promotional messages.</li>
        <li>Use the Service to recruit, scam, or defraud other users (catfishing, romance scams, financial scams).</li>
        <li>Impersonate another person.</li>
        <li>Upload or transmit malware, scripts that attempt to disrupt the Service, or attempt to gain unauthorised access.</li>
        <li>Scrape, copy, or republish other users' profiles, photos, or content.</li>
        <li>Use the Service to harvest contact details or build alternative platforms.</li>
        <li>Circumvent rate limits, abuse-prevention measures, or content filters.</li>
        <li>Use automated tools (bots) for any activity beyond what's permitted by our public API (which is currently not offered).</li>
      </ul>
      <p style={para}>
        Violations result in warnings, temporary suspension, or permanent
        account termination, at our discretion. Repeated or serious violations
        (CSAM, violent threats, fraud) are immediate-termination offences.
      </p>

      <h2 className="serif" style={h2}>9. Account suspension + termination</h2>
      <ul style={ul}>
        <li>You can delete your account at any time. See <Link to="/account/delete" style={inlineLink}>wydm.app/account/delete</Link>.</li>
        <li>We may suspend or terminate your account if you violate these Terms, expose us or other users to legal risk, or use the Service in a way that disrupts other users.</li>
        <li>We'll usually warn before suspending. For severe violations (CSAM, credible threats of violence, large-scale abuse) we suspend immediately and notify by email.</li>
        <li>Upon termination, your account is treated as soft-deleted (per the deletion process documented at <Link to="/account/delete" style={inlineLink}>/account/delete</Link>) unless we determine permanent removal is warranted to protect other users.</li>
      </ul>

      <h2 className="serif" style={h2}>10. Service availability</h2>
      <p style={para}>
        We aim for high availability but do not guarantee uninterrupted service.
        We may perform maintenance, deploy updates, or change features without
        notice. We're not liable for losses arising from service interruptions,
        data loss (use the data export to maintain your own copies if needed),
        or third-party processor outages.
      </p>

      <h2 className="serif" style={h2}>11. Disclaimers + limitation of liability</h2>
      <p style={para}>
        The Service is provided "as is" without warranties of any kind, express
        or implied, including merchantability, fitness for purpose, or
        non-infringement. We don't warrant that the Service will meet your
        expectations, be error-free, or that ratings + AI reviews are accurate
        characterisations of any person.
      </p>
      <p style={para}>
        To the maximum extent permitted by law, our total liability to you for
        all claims arising out of or relating to the Service is limited to the
        greater of (a) the amount you paid us in the 12 months preceding the
        claim, or (b) £100. We are not liable for indirect, incidental, special,
        consequential, or punitive damages.
      </p>
      <p style={para}>
        Nothing in these Terms excludes liability that cannot be excluded under
        UK law (including death or personal injury caused by negligence, or
        fraud).
      </p>

      <h2 className="serif" style={h2}>12. Changes to these Terms</h2>
      <p style={para}>
        We may update these Terms occasionally. Material changes will be
        notified by email or in-app at least 30 days before they take effect.
        Continued use after the effective date constitutes acceptance. If you
        don't agree, delete your account before the effective date.
      </p>

      <h2 className="serif" style={h2}>13. Governing law + disputes</h2>
      <p style={para}>
        These Terms are governed by the laws of England and Wales. Disputes
        will be resolved exclusively in the courts of England and Wales, except
        where local consumer-protection law gives you the right to bring a
        claim in your home jurisdiction (e.g. UK consumers and EU consumers can
        rely on those rights).
      </p>

      <h2 className="serif" style={h2}>14. Contact</h2>
      <p style={para}>
        Callo Apps Ltd, United Kingdom.<br />
        Email: <a href="mailto:support@wydm.app" style={inlineLink}>support@wydm.app</a><br />
      </p>

      <div style={footer}>
        <Link to="/privacy" style={inlineLink}>Privacy Policy</Link>{' · '}
        <Link to="/account/delete" style={inlineLink}>Delete your account</Link>{' · '}
        <Link to="/" style={inlineLink}>Home</Link>
      </div>
    </main>
  );
}

// ── styles ────────────────────────────────────────────────────────────

const page: React.CSSProperties = { maxWidth: 760, margin: '0 auto', padding: '64px 24px' };
const banner: React.CSSProperties = {
  marginTop: 24, padding: '14px 18px',
  background: 'rgba(232,106,76,0.06)', border: '1px solid rgba(232,106,76,0.2)', borderRadius: 12,
  fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.5,
};
const h1: React.CSSProperties = { fontSize: 44, fontWeight: 500, lineHeight: 1.1, marginTop: 32, marginBottom: 8, letterSpacing: -0.5 };
const meta: React.CSSProperties = { fontSize: 13, color: 'var(--ink-3)', marginBottom: 36, fontFamily: 'ui-monospace, monospace' };
const h2: React.CSSProperties = { fontSize: 24, fontWeight: 500, fontStyle: 'italic', marginTop: 40, marginBottom: 14 };
const para: React.CSSProperties = { fontSize: 15, color: 'var(--ink-2)', lineHeight: 1.65, marginBottom: 14 };
const ul: React.CSSProperties = { paddingLeft: 22, margin: '0 0 14px 0', fontSize: 15, lineHeight: 1.7, color: 'var(--ink)' };
const ulNested: React.CSSProperties = { paddingLeft: 18, margin: '8px 0', fontSize: 14, lineHeight: 1.65 };
const inlineLink: React.CSSProperties = { color: 'var(--coral)', textDecoration: 'underline', fontWeight: 600 };
const footer: React.CSSProperties = { marginTop: 48, paddingTop: 24, borderTop: '1px solid var(--line)', fontSize: 13, color: 'var(--ink-3)' };
